import { SkillProposalReview } from "../../components/chat/SkillProposalReview";
import { t } from "../../i18n";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  CheckCircle2,
  ExternalLink,
  LockKeyhole,
  Plus,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import type {
  AgentDefinition,
  AgentWorkspaceDefinition,
  AvailableToolDefinition,
  GitRepositoryDefinition,
  McpServerDefinition,
  SkillDefinition,
} from "@lumen/shared";
import { api } from "../../api";

type Action = {
  action: string;
  title: string;
  description: string;
  settingsPath: string;
  secureHandoff: boolean;
  approvalRequired: boolean;
};

type Catalog = {
  actions: Action[];
  resourceCounts: Record<string, number>;
  security: {
    secretsStayInSecureUi: boolean;
    hostKeysMustBePinned: boolean;
    highRiskActionsRequireApproval: boolean;
    executableExtensionsRequireReview: boolean;
  };
};

type Request = Action & {
  id: string;
  status:
    "pending" | "approved" | "completed" | "cancelled" | "failed" | "expired";
  request: Record<string, unknown>;
  result: Record<string, unknown> | null;
  error: string | null;
  source: "agent" | "user";
  expiresAt: string;
  completedAt: string | null;
  createdAt: string;
};

type Preferences = {
  defaultAgentId: string | null;
  defaultWorkspaceId: string | null;
  defaultRepositoryId: string | null;
  toolIds: string[];
  skillIds: string[];
  mcpServerIds: string[];
};

type Options = {
  agents: AgentDefinition[];
  workspaces: AgentWorkspaceDefinition[];
  repositories: GitRepositoryDefinition[];
  tools: AvailableToolDefinition[];
  skills: SkillDefinition[];
  mcpServers: McpServerDefinition[];
};

function formatDetails(request: Record<string, unknown>) {
  const entries = Object.entries(request);
  if (!entries.length) return "No additional setup details were supplied.";
  return entries
    .map(
      ([key, value]) =>
        `${key}: ${typeof value === "string" ? value : JSON.stringify(value)}`,
    )
    .join("\n");
}

function settingsPathForRequest(request: Request) {
  if (request.action !== "revoke_personal_connection")
    return request.settingsPath;
  const resourceType = request.request.resourceType;
  return resourceType === "skill"
    ? "/settings/skills"
    : resourceType === "mcp_server"
      ? "/settings/mcp"
      : resourceType === "integration"
        ? "/settings/integrations"
        : resourceType === "workspace" || resourceType === "proxmox_integration"
          ? "/settings/agent-workspaces"
          : resourceType === "repository"
            ? "/settings/repositories"
            : request.settingsPath;
}

export function PersonalizationPage() {
  const [searchParams] = useSearchParams();
  const requestedId = searchParams.get("requestId");
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [requests, setRequests] = useState<Request[]>([]);
  const [preferences, setPreferences] = useState<Preferences>({
    defaultAgentId: null,
    defaultWorkspaceId: null,
    defaultRepositoryId: null,
    toolIds: [],
    skillIds: [],
    mcpServerIds: [],
  });
  const [options, setOptions] = useState<Options>({
    agents: [],
    workspaces: [],
    repositories: [],
    tools: [],
    skills: [],
    mcpServers: [],
  });
  const [selectedAction, setSelectedAction] = useState("");
  const [details, setDetails] = useState("{}");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [nextCatalog, nextRequests, nextPreferences] = await Promise.all([
      api<Catalog>("/personalization/catalog"),
      api<Request[]>("/personalization/requests"),
      api<Preferences>("/me/personalization-preferences"),
    ]);
    setCatalog(nextCatalog);
    setRequests(nextRequests);
    setPreferences(nextPreferences);
    const results = await Promise.allSettled([
      api<AgentDefinition[]>("/agents/available"),
      api<AgentWorkspaceDefinition[]>("/agent-workspaces/available"),
      api<GitRepositoryDefinition[]>("/git-repositories"),
      api<AvailableToolDefinition[]>("/tools/available"),
      api<SkillDefinition[]>("/skills"),
      api<McpServerDefinition[]>("/mcp-servers/available"),
    ]);
    const value = <T,>(index: number): T[] =>
      results[index]?.status === "fulfilled"
        ? (results[index] as PromiseFulfilledResult<T[]>).value
        : [];
    setOptions({
      agents: value<AgentDefinition>(0),
      workspaces: value<AgentWorkspaceDefinition>(1),
      repositories: value<GitRepositoryDefinition>(2),
      tools: value<AvailableToolDefinition>(3),
      skills: value<SkillDefinition>(4),
      mcpServers: value<McpServerDefinition>(5),
    });
    if (!selectedAction && nextCatalog.actions.length)
      setSelectedAction(nextCatalog.actions[0].action);
  }, [selectedAction]);

  const updatePreferences = <K extends keyof Preferences>(
    key: K,
    value: Preferences[K],
  ) => setPreferences((current) => ({ ...current, [key]: value }));
  const togglePreference = (
    key: "toolIds" | "skillIds" | "mcpServerIds",
    id: string,
  ) =>
    updatePreferences(
      key,
      preferences[key].includes(id)
        ? preferences[key].filter((current) => current !== id)
        : [...preferences[key], id],
    );
  const savePreferences = async () => {
    setBusy(true);
    setMessage("");
    setError("");
    try {
      const saved = await api<Preferences>("/me/personalization-preferences", {
        method: "PATCH",
        body: JSON.stringify(preferences),
      });
      setPreferences(saved);
      setMessage("Personal defaults saved for future conversations.");
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not save preferences",
      );
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load().catch((reason) =>
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not load personalization",
      ),
    );
  }, [load]);

  const highlighted = useMemo(
    () => requests.find((request) => request.id === requestedId) ?? requests[0],
    [requests, requestedId],
  );
  const linkedCompletionActions = new Set([
    "customize_agent",
    "create_personal_skill",
    "configure_mcp",
    "configure_integration",
    "create_ssh_workspace",
    "configure_proxmox",
    "configure_repository",
    "create_connector",
    "create_automation_draft",
    "create_executable_skill",
    "create_custom_tool",
    "revoke_personal_connection",
  ]);

  const createPlan = async () => {
    setBusy(true);
    setMessage("");
    setError("");
    try {
      const request = JSON.parse(details) as Record<string, unknown>;
      await api<Request>("/personalization/requests", {
        method: "POST",
        body: JSON.stringify({ action: selectedAction, request }),
      });
      setMessage("Setup plan created.");
      setDetails("{}");
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not create setup plan",
      );
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (request: Request) => {
    setBusy(true);
    try {
      await api<Request>(`/personalization/requests/${request.id}/cancel`, {
        method: "POST",
      });
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not cancel setup plan",
      );
    } finally {
      setBusy(false);
    }
  };

  const complete = async (request: Request) => {
    setBusy(true);
    try {
      await api<Request>(`/personalization/requests/${request.id}/complete`, {
        method: "POST",
        body: JSON.stringify({
          result: { completedFrom: request.settingsPath },
        }),
      });
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not complete setup plan",
      );
    } finally {
      setBusy(false);
    }
  };

  const approve = async (request: Request) => {
    setBusy(true);
    setError("");
    try {
      await api<Request>(`/personalization/requests/${request.id}/approve`, {
        method: "POST",
      });
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not approve setup plan",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-w-0 flex-1 overflow-y-auto p-5 md:p-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-7">
          <p className="text-xs font-bold tracking-[.18em] text-lumen-600 uppercase">
            {t("Personalization")}
          </p>
          <h1 className="mt-2 text-3xl font-bold">
            {t("Set up Lumen with your agent")}
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-stone-600 dark:text-stone-300">
            {t(
              "Agents can create personal skills and apply defaults directly. Connections and reviewed extensions use setup plans; secrets and OAuth consent stay in secure settings flows.",
            )}
          </p>
        </div>

        {requestedId &&
          highlighted?.id === requestedId &&
          ["create_personal_skill", "create_executable_skill"].includes(
            highlighted.action,
          ) && (
            <section
              className="panel mb-6 p-5"
              aria-label={t("Review proposed skill")}
            >
              <h2 className="text-lg font-bold">
                {t("Review proposed skill")}
              </h2>
              <SkillProposalReview requestId={highlighted.id} onChange={load} />
            </section>
          )}
        {requestedId &&
          !requests.some((request) => request.id === requestedId) &&
          catalog && (
            <p role="alert" className="mb-4 text-sm">
              {t("The requested setup plan was not found.")}
            </p>
          )}

        <div className="mb-6 grid gap-3 md:grid-cols-4">
          <div className="panel p-4">
            <div className="text-xs text-stone-500">{t("Skills")}</div>
            <div className="mt-1 text-2xl font-bold">
              {catalog?.resourceCounts.skills ?? "—"}
            </div>
          </div>
          <div className="panel p-4">
            <div className="text-xs text-stone-500">{t("MCP servers")}</div>
            <div className="mt-1 text-2xl font-bold">
              {catalog?.resourceCounts.mcpServers ?? "—"}
            </div>
          </div>
          <div className="panel p-4">
            <div className="text-xs text-stone-500">{t("Integrations")}</div>
            <div className="mt-1 text-2xl font-bold">
              {catalog?.resourceCounts.integrations ?? "—"}
            </div>
          </div>
          <div className="panel p-4">
            <div className="text-xs text-stone-500">{t("Workspaces")}</div>
            <div className="mt-1 text-2xl font-bold">
              {catalog?.resourceCounts.workspaces ?? "—"}
            </div>
          </div>
        </div>

        <div className="mb-6 grid gap-4 md:grid-cols-3">
          <div className="panel p-4">
            <LockKeyhole className="text-lumen-600" size={20} />
            <h2 className="mt-3 font-semibold">{t("Secrets stay private")}</h2>
            <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">
              {t(
                "Passwords, keys, tokens, and passphrases are rejected from agent\n              plans and entered only in secure forms.",
              )}
            </p>
          </div>
          <div className="panel p-4">
            <ShieldCheck className="text-lumen-600" size={20} />
            <h2 className="mt-3 font-semibold">
              {t("Connections are governed")}
            </h2>
            <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">
              {t(
                "SSH host keys, network policies, connector allowlists, quotas, and\n              tenant permissions still apply.",
              )}
            </p>
          </div>
          <div className="panel p-4">
            <CheckCircle2 className="text-lumen-600" size={20} />
            <h2 className="mt-3 font-semibold">
              {t("High-risk actions need review")}
            </h2>
            <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">
              {t(
                "Executable extensions, private infrastructure, integrations, and\n              publishing workflows remain approval-gated.",
              )}
            </p>
          </div>
        </div>

        <section className="panel mb-6 p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold">{t("Personal defaults")}</h2>
              <p className="mt-1 text-sm text-stone-500">
                {t(
                  "These are applied when a new conversation has no explicit\n                selections.",
                )}
              </p>
            </div>
            <button
              className="button primary"
              disabled={busy}
              onClick={() => void savePreferences()}
            >
              {t("Save defaults")}
            </button>
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            <label className="text-sm font-semibold">
              {t("Default agent")}
              <select
                className="input mt-2 w-full"
                value={preferences.defaultAgentId ?? ""}
                onChange={(event) =>
                  updatePreferences(
                    "defaultAgentId",
                    event.target.value || null,
                  )
                }
              >
                <option value="">{t("Use Lumen’s normal default")}</option>
                {options.agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-semibold">
              {t("Default workspace")}
              <select
                className="input mt-2 w-full"
                value={preferences.defaultWorkspaceId ?? ""}
                onChange={(event) =>
                  updatePreferences(
                    "defaultWorkspaceId",
                    event.target.value || null,
                  )
                }
              >
                <option value="">{t("No default workspace")}</option>
                {options.workspaces.map((workspace) => (
                  <option key={workspace.id} value={workspace.id}>
                    {workspace.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-semibold">
              {t("Default repository")}
              <select
                className="input mt-2 w-full"
                value={preferences.defaultRepositoryId ?? ""}
                onChange={(event) =>
                  updatePreferences(
                    "defaultRepositoryId",
                    event.target.value || null,
                  )
                }
              >
                <option value="">{t("No default repository")}</option>
                {options.repositories.map((repository) => (
                  <option key={repository.id} value={repository.id}>
                    {repository.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            {(
              [
                [
                  "toolIds",
                  "Default tools",
                  options.tools.map((tool) => ({
                    id: tool.id,
                    name: tool.displayName || tool.name,
                  })),
                ],
                [
                  "skillIds",
                  "Default skills",
                  options.skills.map((skill) => ({
                    id: skill.id,
                    name: skill.name,
                  })),
                ],
                [
                  "mcpServerIds",
                  "Default MCP servers",
                  options.mcpServers.map((server) => ({
                    id: server.id,
                    name: server.name,
                  })),
                ],
              ] as const
            ).map(([key, title, rows]) => (
              <div key={key}>
                <h3 className="text-sm font-semibold">{title}</h3>
                <div className="mt-2 max-h-36 space-y-2 overflow-y-auto rounded-lg border border-stone-200 p-3 dark:border-white/10">
                  {!rows.length && (
                    <p className="text-xs text-stone-500">
                      {t("None available")}
                    </p>
                  )}
                  {rows.map((row) => (
                    <label
                      key={row.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={preferences[key].includes(row.id)}
                        onChange={() => togglePreference(key, row.id)}
                      />
                      <span className="truncate">{row.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {(message || error) && (
          <div
            className={`mb-4 rounded-xl border p-3 text-sm ${error ? "border-red-300 bg-red-50 text-red-800" : "border-emerald-300 bg-emerald-50 text-emerald-800"}`}
          >
            {error || message}
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[1fr_1.15fr]">
          <section className="panel p-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold">
                  {t("Create a setup plan")}
                </h2>
                <p className="mt-1 text-sm text-stone-500">
                  {t("The agent normally creates these plans for you.")}
                </p>
              </div>
              <Plus size={18} />
            </div>
            <label className="mt-5 block text-sm font-semibold">
              {t("Action")}
              <select
                className="input mt-2 w-full"
                value={selectedAction}
                onChange={(event) => setSelectedAction(event.target.value)}
              >
                {catalog?.actions.map((action) => (
                  <option key={action.action} value={action.action}>
                    {action.title}
                  </option>
                ))}
              </select>
            </label>
            <label className="mt-4 block text-sm font-semibold">
              {t("Non-secret details")}
              <textarea
                className="input mt-2 min-h-32 w-full font-mono text-xs"
                value={details}
                onChange={(event) => setDetails(event.target.value)}
                spellCheck={false}
              />
            </label>
            <p className="mt-2 text-xs text-stone-500">
              {t(
                "Do not paste credentials here. The API rejects secret-bearing\n              fields.",
              )}
            </p>
            <button
              className="button primary mt-4"
              disabled={busy || !selectedAction}
              onClick={() => void createPlan()}
            >
              {t("Create plan")}
            </button>
          </section>

          <section className="panel p-5">
            <h2 className="text-lg font-bold">{t("Agent setup plans")}</h2>
            <div className="mt-4 space-y-3">
              {!requests.length && (
                <p className="text-sm text-stone-500">
                  {t("No setup plans yet. Ask the agent to personalize Lumen.")}
                </p>
              )}
              {requests
                .filter(
                  (request) =>
                    !(
                      request.id === requestedId &&
                      [
                        "create_personal_skill",
                        "create_executable_skill",
                      ].includes(request.action)
                    ),
                )
                .map((request) => (
                  <article
                    key={request.id}
                    className={`rounded-xl border p-4 ${request.id === highlighted?.id ? "border-lumen-400 bg-lumen-50/50 dark:bg-lumen-900/10" : "border-stone-200 dark:border-white/10"}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold">{request.title}</h3>
                        <p className="mt-1 text-xs text-stone-500">
                          {request.description}
                        </p>
                      </div>
                      <span className="rounded-full bg-stone-100 px-2 py-1 text-[11px] font-semibold uppercase dark:bg-white/10">
                        {request.status}
                      </span>
                    </div>
                    <pre className="mt-3 rounded-lg bg-stone-50 p-3 text-xs whitespace-pre-wrap text-stone-600 dark:bg-black/20 dark:text-stone-300">
                      {formatDetails(request.request)}
                    </pre>
                    {[
                      "create_personal_skill",
                      "create_executable_skill",
                    ].includes(request.action) ? (
                      <SkillProposalReview
                        requestId={request.id}
                        onChange={load}
                      />
                    ) : (
                      (request.status === "pending" ||
                        request.status === "approved") && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {request.status === "pending" &&
                          request.approvalRequired ? (
                            <button
                              className="button primary"
                              disabled={busy}
                              onClick={() => void approve(request)}
                            >
                              <ShieldCheck size={14} />
                              {t("Approve setup")}
                            </button>
                          ) : (
                            <>
                              <a
                                className="button primary"
                                href={`${settingsPathForRequest(request)}${settingsPathForRequest(request).includes("?") ? "&" : "?"}personalizationRequestId=${encodeURIComponent(request.id)}`}
                              >
                                <ExternalLink size={14} />
                                {t("Open secure setup")}
                              </a>
                              {!linkedCompletionActions.has(request.action) && (
                                <button
                                  className="button"
                                  disabled={busy}
                                  onClick={() => void complete(request)}
                                >
                                  <CheckCircle2 size={14} />
                                  {t("Apply settings")}
                                </button>
                              )}
                            </>
                          )}
                          <button
                            className="button"
                            disabled={busy}
                            onClick={() => void cancel(request)}
                          >
                            <XCircle size={14} />
                            {t("Cancel")}
                          </button>
                        </div>
                      )
                    )}
                    {request.error && (
                      <p className="mt-2 text-sm text-red-700">
                        {request.error}
                      </p>
                    )}
                  </article>
                ))}
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
